/**
 * Capacitor-specific Cognito session reader.
 *
 * Bypasses Amplify's `fetchAuthSession` on Capacitor and reads tokens
 * directly from localStorage. This exists because Capacitor cannot use
 * Amplify's `signInWithRedirect` (the WebView blocks `window.location.assign`),
 * so we own the OAuth dance ourselves (`startSsoRedirect` →
 * `exchangeCodeOnCapacitor` → `storeCognitoTokens`). Owning the writes means
 * we should own the reads too — half-using Amplify is the leaky path that
 * caused tokens to be wiped mid-login: Amplify's internal
 * refresh fails on our manually-stored tokens because we don't write
 * `userData`/device-tracking metadata, and `handleErrors` calls
 * `clearTokens()` to "clean up" — destroying the session we just stored.
 *
 * Web is untouched: it still goes through Amplify's `fetchAuthSession`.
 *
 * Token refresh: when the stored ID token is near expiration, this reader
 * POSTs `grant_type=refresh_token` to Cognito's `/oauth2/token` endpoint
 * and rewrites localStorage with the fresh tokens — including any rotated
 * refresh token Cognito returns. Only an explicit `invalid_grant` clears
 * local tokens; network blips and 5xx leave storage intact so the next
 * attempt can recover without forcing re-authentication.
 */
import { cognitoConfig } from "./config";

const STORAGE_PREFIX = "CognitoIdentityServiceProvider";
/** Refresh ID token if it expires within this many seconds. */
const EXPIRATION_SKEW_SECONDS = 60;

interface JWTLike {
  payload: Record<string, unknown>;
  toString(): string;
}

/**
 * Shape-compatible subset of Amplify's `AuthSession` so callers reading
 * `session.tokens?.idToken?.payload?.email`, `idToken.toString()`, etc. work
 * unchanged.
 */
export interface CapacitorAuthSession {
  tokens?: {
    idToken?: JWTLike;
    accessToken?: JWTLike;
    refreshToken?: string;
  };
}

function decodeJwt(token: string): JWTLike {
  const [, payloadSegment = ""] = token.split(".");
  const normalized = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
  return {
    payload,
    toString: () => token,
  };
}

function isExpiredOrNearlyExpired(payload: Record<string, unknown>): boolean {
  const { exp } = payload;
  if (typeof exp !== "number") {
    return true;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp - EXPIRATION_SKEW_SECONDS < nowSeconds;
}

function getStorageKeys(username: string) {
  const prefix = `${STORAGE_PREFIX}.${cognitoConfig.clientId}`;
  return {
    idToken: `${prefix}.${username}.idToken`,
    accessToken: `${prefix}.${username}.accessToken`,
    refreshToken: `${prefix}.${username}.refreshToken`,
    clockDrift: `${prefix}.${username}.clockDrift`,
    lastAuthUser: `${prefix}.LastAuthUser`,
  };
}

function getLastAuthUser(): string | undefined {
  const key = `${STORAGE_PREFIX}.${cognitoConfig.clientId}.LastAuthUser`;
  return localStorage.getItem(key) ?? undefined;
}

function clearStoredTokens(username: string): void {
  const keys = getStorageKeys(username);
  localStorage.removeItem(keys.idToken);
  localStorage.removeItem(keys.accessToken);
  localStorage.removeItem(keys.refreshToken);
  localStorage.removeItem(keys.clockDrift);
  localStorage.removeItem(keys.lastAuthUser);
}

type RefreshResult =
  | { kind: "success"; idToken: string; accessToken: string; refreshToken: string }
  | { kind: "invalid_grant" }
  | { kind: "transient" };

async function refreshTokensWithCognito(refreshToken: string): Promise<RefreshResult> {
  let response: Response;
  try {
    response = await fetch(`https://${cognitoConfig.oauthDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: cognitoConfig.clientId,
        refresh_token: refreshToken,
      }),
    });
  } catch {
    return { kind: "transient" };
  }

  if (response.ok) {
    // Cognito's `/oauth2/token` response uses snake_case per the OAuth 2.0 spec.
    const data = (await response.json().catch(() => undefined)) as
      | Record<string, unknown>
      | undefined;
    const idToken = typeof data?.id_token === "string" ? data.id_token : undefined;
    const accessToken = typeof data?.access_token === "string" ? data.access_token : undefined;
    if (!idToken || !accessToken) {
      return { kind: "transient" };
    }
    // When the user-pool client has refresh-token rotation enabled, the
    // response carries a fresh `refresh_token` and Cognito invalidates the
    // one we just sent. We must persist the rotated value or the next
    // refresh fails with `invalid_grant`, taking the session with it.
    const rotated = typeof data?.refresh_token === "string" ? data.refresh_token : refreshToken;
    return { kind: "success", idToken, accessToken, refreshToken: rotated };
  }

  if (response.status === 400) {
    const body = (await response.json().catch(() => ({}))) as { error?: unknown };
    if (body.error === "invalid_grant") {
      return { kind: "invalid_grant" };
    }
  }

  return { kind: "transient" };
}

function persistRefreshedTokens(
  username: string,
  tokens: { idToken: string; accessToken: string; refreshToken: string }
): void {
  const keys = getStorageKeys(username);
  localStorage.setItem(keys.idToken, tokens.idToken);
  localStorage.setItem(keys.accessToken, tokens.accessToken);
  localStorage.setItem(keys.refreshToken, tokens.refreshToken);
}

/**
 * Promise-deduplication mutex for concurrent session reads.
 *
 * Cognito uses refresh-token rotation: once a refresh token is consumed, the
 * previous value is invalidated. On Capacitor there is no Web Locks API
 * available to serialize across surfaces, and the cross-tab lock in
 * `cognitoSessionLock.ts` only protects the Amplify branch. Without this
 * mutex, multiple parallel callers (React Query hooks, axios interceptors,
 * etc.) each enter `readCapacitorCognitoSession` near token expiration and
 * each POSTs `/oauth2/token` with the same refresh token. One wins, the rest
 * receive `400 invalid_grant`, Amplify retries, and the user is logged out.
 *
 * Sharing a single in-flight promise collapses N concurrent callers into one
 * network refresh.
 *
 * `forceRefresh: true` callers bypass this dedupe: if a non-force read is
 * in flight, it may not actually refresh (tokens still look fresh) and would
 * return the same stale tokens that just produced a 401, causing an axios
 * retry loop. Force callers must get a guaranteed-fresh session.
 */
let inFlightSession: Promise<CapacitorAuthSession> | undefined;

/**
 * Returns the current Cognito session by reading directly from localStorage.
 * Refreshes tokens against Cognito's `/oauth2/token` endpoint if the stored
 * ID token is near expiration. Returns an empty session if no tokens exist
 * or refresh fails.
 *
 * Concurrent callers share a single in-flight read (see `inFlightSession`).
 *
 * Shape-compatible with Amplify's `fetchAuthSession` for the fields callers
 * actually use (`tokens.idToken.payload`, `tokens.idToken.toString()`,
 * `tokens.accessToken`).
 */
export async function readCapacitorCognitoSession(options?: {
  forceRefresh?: boolean;
}): Promise<CapacitorAuthSession> {
  if (inFlightSession && !options?.forceRefresh) {
    return inFlightSession;
  }

  inFlightSession = doReadCapacitorCognitoSession(options).finally(() => {
    inFlightSession = undefined;
  });
  return inFlightSession;
}

async function doReadCapacitorCognitoSession(options?: {
  forceRefresh?: boolean;
}): Promise<CapacitorAuthSession> {
  const username = getLastAuthUser();
  if (!username) {
    return {};
  }

  const keys = getStorageKeys(username);
  const idTokenString = localStorage.getItem(keys.idToken);
  const accessTokenString = localStorage.getItem(keys.accessToken);
  const refreshToken = localStorage.getItem(keys.refreshToken) ?? undefined;

  if (!idTokenString || !accessTokenString) {
    return {};
  }

  const idToken = decodeJwt(idTokenString);
  const accessToken = decodeJwt(accessTokenString);

  const needsRefresh =
    options?.forceRefresh ||
    isExpiredOrNearlyExpired(idToken.payload) ||
    isExpiredOrNearlyExpired(accessToken.payload);

  if (!needsRefresh) {
    return { tokens: { idToken, accessToken, refreshToken } };
  }

  if (!refreshToken) {
    clearStoredTokens(username);
    return {};
  }

  const refreshed = await refreshTokensWithCognito(refreshToken);
  if (refreshed.kind === "invalid_grant") {
    clearStoredTokens(username);
    return {};
  }
  if (refreshed.kind === "transient") {
    // Network blip, Cognito 5xx, or any non-`invalid_grant` response. The
    // refresh token may still be valid — leave storage intact so the next
    // attempt can retry rather than forcing the user to re-authenticate.
    return {};
  }

  persistRefreshedTokens(username, refreshed);
  return {
    tokens: {
      idToken: decodeJwt(refreshed.idToken),
      accessToken: decodeJwt(refreshed.accessToken),
      refreshToken: refreshed.refreshToken,
    },
  };
}
